/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import {
  Modal,
  UnorderedList,
  ListItem,
  TextInput,
  TextArea
} from 'carbon-components-react';
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useFormHelpers } from '../../../hooks/useFormHelpers';
import { sharedMessages } from '../../messages';
import { MODAL_STATUS } from '../constants';
import { duplicateCheck } from '../phone-helpers';
import { FIELDS } from '../schema';
import { normalizePhoneNumber } from './helpers';
import { PhoneAddModal as messages } from './messages';

interface PhoneAddModalProps {
  addModalStatus: string;
  newNumAppended: boolean;
  setAddModalStatus: (state: string) => void;
  newNumIndex: number;
  readOnly?: boolean;
}
export const PhoneAddModal = ({
  addModalStatus,
  newNumAppended,
  setAddModalStatus,
  newNumIndex,
  readOnly
}: PhoneAddModalProps) => {
  const { formatMessage: t } = useIntl();

  const formMethods = useFormContext();
  const {
    getFieldValue,
    errorMessageIntl,
    isValid,
    updateFieldValue,
    registerInputField
  } = useFormHelpers(formMethods);

  const watchedFields = getFieldValue(FIELDS.PHONE_NUMBERS);
  const newNumId = newNumIndex - 1;

  const [newNumber, setNewNumber] = useState<string>('');
  const [newNormalizedNum, setNewNormalizedNum] = useState<string>('');
  const [newDescription, setNewDescription] = useState<string>('');
  const phoneNumberChangeState = useState<boolean>(false);

  const fieldsInvalid =
    !isValid(FIELDS.PHONE_NUMBER_NUMBER(newNumId)) ||
    !isValid(FIELDS.PHONE_NUMBER_NAME(newNumId));
  const dupesExist =
    newNormalizedNum !== ''
      ? duplicateCheck(watchedFields, newNormalizedNum)
      : false;

  const handleSaveNormalizedNum = () => {
    // Save phone number as its normalized value
    const normalizedInput = normalizePhoneNumber(newNumber);
    setNewNormalizedNum(normalizedInput);
    // Only update if the Normalized input is not ''
    if (newNumAppended) {
      updateFieldValue(FIELDS.PHONE_NUMBER_NUMBER(newNumId), normalizedInput);
    }
    // Checking if number exists in phone numbers table
    for (let i = 0; i < watchedFields?.length - 1; i++) {
      const phone = watchedFields[i].phone_number;
      if (phone === newNormalizedNum) {
        setAddModalStatus(MODAL_STATUS.CANCELLED);
        return;
      }
    }
    // Adding new unique number to phone numbers table
    setAddModalStatus(MODAL_STATUS.CLOSED);
  };

  // Update the normalized phone number & phone number field value when input changes
  useEffect(() => {
    const normalizedInput = normalizePhoneNumber(newNumber);
    setNewNormalizedNum(normalizedInput);
    if (newNumber) {
      updateFieldValue(FIELDS.PHONE_NUMBER_NUMBER(newNumId), newNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newNumber]);

  // Update the new phone number entry when description changes
  useEffect(() => {
    if (newNumAppended) {
      updateFieldValue(FIELDS.PHONE_NUMBER_NAME(newNumId), newDescription);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newDescription]);

  return (
    <div>
      {ReactDOM.createPortal(
        <Modal
          className="PhoneModal__addPhoneNumber"
          modalHeading={t(messages.title)}
          open={addModalStatus === MODAL_STATUS.OPEN}
          primaryButtonText={t(sharedMessages.add)}
          primaryButtonDisabled={
            !newNormalizedNum || fieldsInvalid || dupesExist
          }
          secondaryButtonText={t(sharedMessages.cancel)}
          onRequestSubmit={handleSaveNormalizedNum}
          onRequestClose={() => setAddModalStatus(MODAL_STATUS.CANCELLED)}
          onSecondarySubmit={() => setAddModalStatus(MODAL_STATUS.CANCELLED)}>
          <div className="PhoneModal__content">
            <div className="PhoneModal__instruction">
              <div>{t(messages.subtitle)}</div>
              <div className="PhoneModal__validPhoneCharacters">
                <UnorderedList>
                  <ListItem>
                    <FormattedMessage {...messages.phoneValidCharacterOne} />
                  </ListItem>
                  <ListItem>
                    <FormattedMessage {...messages.phoneValidCharacterTwo} />
                  </ListItem>
                  <ListItem>
                    <FormattedMessage {...messages.phoneValidCharacterThree} />
                  </ListItem>
                </UnorderedList>
              </div>
            </div>
            <TextInput
              {...registerInputField({
                field: FIELDS.PHONE_NUMBER_NUMBER(newNumId),
                isPasswordField: false,
                changeState: phoneNumberChangeState,
                labelText: messages.phoneNumberLabel,
                placeholderText: messages.phoneNumberPlaceholder,
                readOnly
              })}
              value={newNumber}
              invalid={
                !isValid(FIELDS.PHONE_NUMBER_NUMBER(newNumId)) || dupesExist
              }
              invalidText={
                dupesExist
                  ? t(messages.duplicateNumber)
                  : errorMessageIntl(FIELDS.PHONE_NUMBER_NUMBER(newNumId))
              }
              onChange={e => setNewNumber(e.target.value)}
            />
            <div className="PhoneModal__spacer8" />
            <TextArea
              id="pm-descInput"
              labelText={t(messages.phoneDescriptionLabel)}
              placeholder={t(messages.phoneDescriptionPlaceholder)}
              value={newDescription}
              invalid={!isValid(FIELDS.PHONE_NUMBER_NAME(newNumId))}
              invalidText={errorMessageIntl(FIELDS.PHONE_NUMBER_NAME(newNumId))}
              onChange={e => setNewDescription(e.target.value)}
            />
          </div>
        </Modal>,
        document.body
      )}
    </div>
  );
};
